import React, { useState } from "react";
import {
  Plus, Share2, Trash2, Pin, PinOff, Pencil, X,
  ChevronUp, ChevronDown, Users, Lock, Eye, CheckSquare,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { ChecklistItemRow } from "./ChecklistItemRow";

const SHARING_BADGE = {
  private: { icon: Lock, label: "Privata" },
  users: { icon: Users, label: "Condivisa" },
  org: { icon: Users, label: "Tutta l'org" },
};

/**
 * Card checklist wl- con progresso (n/m + barra sottile), voci spuntabili,
 * creazione voce inline (un click e un invio), azioni owner (rinomina,
 * condividi, pin, elimina, riordino, edit/del voce).
 */
export const ChecklistCard = ({ list, currentUser, pinnedId, onReload, onAskDelete, onOpenShare }) => {
  const isOwner = list.access === "owner";
  const canCheck = list.access === "owner" || list.access === "can_check";
  const [newItem, setNewItem] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(list.titolo);
  const [editingItem, setEditingItem] = useState(null); // {id, testo}

  const pct = list.total_count ? Math.round((list.done_count / list.total_count) * 100) : 0;
  const isPinned = pinnedId === list.id;
  const ShareIcon = (SHARING_BADGE[list.sharing] || SHARING_BADGE.private).icon;

  const addItem = async (e) => {
    e?.preventDefault();
    const testo = newItem.trim();
    if (!testo || adding) return;
    setAdding(true);
    try {
      await api.post(`/checklists/${list.id}/items`, { testo });
      setNewItem("");
      await onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setAdding(false);
    }
  };

  const saveTitle = async () => {
    const t = titleDraft.trim();
    setEditingTitle(false);
    if (!t || t === list.titolo) { setTitleDraft(list.titolo); return; }
    try {
      await api.patch(`/checklists/${list.id}`, { titolo: t });
      await onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
      setTitleDraft(list.titolo);
    }
  };

  const saveItemText = async () => {
    if (!editingItem) return;
    const { id, testo } = editingItem;
    setEditingItem(null);
    const t = (testo || "").trim();
    if (!t) return;
    try {
      await api.patch(`/checklists/${list.id}/items/${id}`, { testo: t });
      await onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const deleteItem = async (itemId) => {
    try {
      await api.delete(`/checklists/${list.id}/items/${itemId}`);
      await onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const move = async (idx, dir) => {
    const ids = list.items.map((i) => i.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try {
      await api.post(`/checklists/${list.id}/reorder`, { item_ids: ids });
      await onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const togglePin = async () => {
    try {
      if (isPinned) await api.post("/checklists/unpin");
      else await api.post(`/checklists/${list.id}/pin`);
      toast.success(isPinned ? "Rimossa dalla dashboard" : "Mostrata in dashboard");
      await onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="wl-card" data-testid={`checklist-card-${list.id}`}>
      <div className="wl-card__body">
        {/* Header: titolo + badge + azioni */}
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="min-w-0 flex-1">
            {editingTitle ? (
              <input
                autoFocus
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={saveTitle}
                onKeyDown={(e) => { if (e.key === "Enter") saveTitle(); if (e.key === "Escape") { setTitleDraft(list.titolo); setEditingTitle(false); } }}
                className="w-full text-base font-semibold text-text-primary bg-subtle rounded-md px-2 py-1 outline-none border border-border"
                data-testid="checklist-title-input"
              />
            ) : (
              <h3
                className={`text-base font-semibold text-text-primary truncate ${isOwner ? "cursor-text" : ""}`}
                onClick={() => isOwner && (setTitleDraft(list.titolo), setEditingTitle(true))}
                title={isOwner ? "Clicca per rinominare" : list.titolo}
                data-testid="checklist-title"
              >
                {list.titolo}
              </h3>
            )}
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="inline-flex items-center gap-1 text-[11px] text-text-muted">
                <ShareIcon size={11} /> {(SHARING_BADGE[list.sharing] || SHARING_BADGE.private).label}
              </span>
              {!isOwner && (
                <>
                  <span className="text-[11px] text-text-muted">di {list.owner_name}</span>
                  <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-subtle text-text-secondary">
                    {list.access === "can_check" ? <><CheckSquare size={10} /> puoi spuntare</> : <><Eye size={10} /> sola lettura</>}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {isOwner && (
              <>
                <IconAction
                  icon={isPinned ? PinOff : Pin}
                  title={isPinned ? "Rimuovi dalla dashboard" : "Mostra in dashboard"}
                  active={isPinned}
                  onClick={togglePin}
                  testId={`checklist-pin-${list.id}`}
                />
                <IconAction icon={Share2} title="Condividi" onClick={() => onOpenShare(list)} testId={`checklist-share-${list.id}`} />
                <IconAction icon={Trash2} title="Elimina lista" danger onClick={() => onAskDelete(list)} testId={`checklist-delete-${list.id}`} />
              </>
            )}
          </div>
        </div>

        {/* Progresso: n/m + barra sottile */}
        <div className="flex items-center gap-3 mb-3" data-testid="checklist-progress">
          <span className="text-xs font-medium text-text-secondary tabular-nums">{list.done_count}/{list.total_count}</span>
          <div className="flex-1 h-1 rounded-full bg-subtle overflow-hidden">
            <div
              className="h-1 rounded-full bg-accent transition-all duration-150"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Voci */}
        {list.items.length === 0 && (
          <div className="text-sm text-text-muted py-2">
            {isOwner ? "Nessuna voce: aggiungi la prima qui sotto." : "Lista ancora vuota."}
          </div>
        )}
        <ul>
          {list.items.map((item, idx) => (
            editingItem?.id === item.id ? (
              <li key={item.id} className="py-1">
                <input
                  autoFocus
                  value={editingItem.testo}
                  onChange={(e) => setEditingItem({ ...editingItem, testo: e.target.value })}
                  onBlur={saveItemText}
                  onKeyDown={(e) => { if (e.key === "Enter") saveItemText(); if (e.key === "Escape") setEditingItem(null); }}
                  className="w-full text-sm text-text-primary bg-subtle rounded-md px-2 py-2 outline-none border border-border"
                  data-testid={`checklist-item-edit-${item.id}`}
                />
              </li>
            ) : (
              <ChecklistItemRow
                key={item.id}
                listId={list.id}
                item={item}
                canCheck={canCheck}
                currentUserId={currentUser?.id}
                onChanged={onReload}
                ownerActions={isOwner ? (
                  <span className="flex items-center gap-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150 shrink-0">
                    <IconAction icon={ChevronUp} title="Sposta su" small disabled={idx === 0} onClick={() => move(idx, -1)} testId={`item-up-${item.id}`} />
                    <IconAction icon={ChevronDown} title="Sposta giù" small disabled={idx === list.items.length - 1} onClick={() => move(idx, 1)} testId={`item-down-${item.id}`} />
                    <IconAction icon={Pencil} title="Modifica testo" small onClick={() => setEditingItem({ id: item.id, testo: item.testo })} testId={`item-edit-${item.id}`} />
                    <IconAction icon={X} title="Elimina voce" small danger onClick={() => deleteItem(item.id)} testId={`item-del-${item.id}`} />
                  </span>
                ) : null}
              />
            )
          ))}
        </ul>

        {/* Aggiunta inline: un click e un invio */}
        {isOwner && (
          <form onSubmit={addItem} className="flex items-center gap-1 mt-2 -ml-2">
            <span className="w-10 h-10 flex items-center justify-center shrink-0">
              <Plus size={16} className="text-text-muted" />
            </span>
            <input
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              placeholder="Aggiungi una voce e premi Invio…"
              className="flex-1 text-sm bg-transparent outline-none py-2 text-text-primary placeholder:text-text-muted border-b border-transparent focus:border-border transition-colors duration-150"
              data-testid={`checklist-add-input-${list.id}`}
            />
          </form>
        )}
      </div>
    </div>
  );
};

const IconAction = ({ icon: Icon, title, onClick, danger, active, small, disabled, testId }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    disabled={disabled}
    onClick={onClick}
    data-testid={testId}
    className={`${small ? "w-7 h-7" : "w-8 h-8"} inline-flex items-center justify-center rounded-md transition-colors duration-150 disabled:opacity-30 ${
      danger ? "text-text-muted hover:text-red-600 hover:bg-red-50"
      : active ? "text-accent bg-accent-subtle"
      : "text-text-muted hover:text-text-primary hover:bg-subtle"
    }`}
  >
    <Icon size={small ? 13 : 15} strokeWidth={1.75} />
  </button>
);
